import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { client, STATUS_META, fmtTimeAST } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { EQUIPMENT_STATUS_OPTIONS } from "@/components/AddEquipmentDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Wrench, DollarSign, ClipboardList, ShieldCheck, Download, Pencil } from "lucide-react";

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "manager" || user?.role === "service_coordinator";
  const [eq, setEq] = useState(null);
  const [area, setArea] = useState(null);
  const [site, setSite] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [tree, setTree] = useState({ areas: [], sites: [], customers: [] });
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    const [es, as, ss, cs, js, ts] = await Promise.all([
      client.get("/equipment"), client.get("/areas"), client.get("/sites"),
      client.get("/customers"), client.get("/jobs"), client.get("/technicians"),
    ]);
    const eqObj = es.data.find(x => x.id === id);
    if (!eqObj) { navigate("/equipment"); return; }
    const a = as.data.find(x => x.id === eqObj.area_id);
    const s = ss.data.find(x => x.id === a?.site_id);
    const c = cs.data.find(x => x.id === s?.customer_id);
    setEq(eqObj); setArea(a); setSite(s); setCustomer(c); setTechs(ts.data);
    setTree({ areas: as.data, sites: ss.data, customers: cs.data });
    setJobs(js.data.filter(j => j.equipment_id === id).sort((x, y) => new Date(y.created_at) - new Date(x.created_at)));
  };

  useEffect(() => { load(); }, [id, navigate]);

  const covActive = eq?.coverage_end_date && new Date(eq.coverage_end_date) >= new Date();

  const stats = useMemo(() => {
    const completed = jobs.filter(j => j.status === "complete");
    const totalLabourHours = completed.reduce((a, j) => a + (Number(j.labour_hours) || 0), 0);
    const partsBillable = completed.flatMap(j => j.parts_used || []).filter(p => !p.covered);
    const partsCovered = completed.flatMap(j => j.parts_used || []).filter(p => p.covered);
    const partsSpend = partsBillable.reduce((a, p) => a + (Number(p.unit_price) || 0) * (Number(p.qty) || 1), 0);
    const partsCoveredValue = partsCovered.reduce((a, p) => a + (Number(p.unit_price) || 0) * (Number(p.qty) || 1), 0);
    return {
      totalJobs: jobs.length,
      completedJobs: completed.length,
      activeJobs: jobs.length - completed.length,
      totalLabourHours,
      partsSpend,
      partsCoveredValue,
    };
  }, [jobs]);

  if (!eq) return <div className="mono text-[12px]" style={{ color: "#5F6B7A" }}>Loading…</div>;

  return (
    <div data-testid="equipment-detail-page">
      <Toaster richColors position="top-right" />
      <button onClick={() => navigate("/equipment")} className="flex items-center gap-1 text-[13px] mb-3" style={{ color: "#5F6B7A" }} data-testid="back-link">
        <ArrowLeft size={14} /> Equipment
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5B6472" }}>{eq.type}</div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>{eq.make} {eq.model}</h1>
          <div className="text-[13.5px] mt-1 mono" style={{ color: "#5B6472" }} data-testid="eq-serial">
            SN {eq.serial_number} · installed {eq.install_date}
          </div>
          <div className="text-[13.5px] mt-1" style={{ color: "#5B6472", lineHeight: 1.5 }}>
            {customer?.name} → {site?.name} → {area?.name}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button className="bf-btn-primary px-4" onClick={() => setEditOpen(true)} data-testid="edit-equipment-button">
              <Pencil size={14} className="mr-2" /> Edit
            </Button>
          )}
          {eq.coverage_type && eq.coverage_type !== "None" ? (
            <span className="bf-chip" style={{ background: covActive ? "#E9F7EE" : "#FDECEC", color: covActive ? "#136B36" : "#B42318", padding: "7px 12px", fontSize: "12px" }}
              data-testid="coverage-badge"
            >
              <ShieldCheck size={13} />
              {covActive ? "COVERAGE ACTIVE" : "COVERAGE EXPIRED"} · {eq.coverage_type} · ends {eq.coverage_end_date}
            </span>
          ) : <span className="bf-chip" style={{ background: "#EEF1F5", color: "#5B6472", padding: "7px 12px", fontSize: "12px" }}>No coverage</span>}
          <span className="bf-chip" style={{ background: eq.supplied_by_us ? "#EAF0FE" : "#EEF1F5", color: eq.supplied_by_us ? "#1E3A8A" : "#5B6472", padding: "7px 12px", fontSize: "12px" }}>
            {eq.supplied_by_us ? "Supplied by us" : "Customer-owned"}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
        <StatCard icon={<ClipboardList size={16} />} label="Service jobs" value={stats.totalJobs} sub={`${stats.completedJobs} complete · ${stats.activeJobs} active`} />
        <StatCard icon={<Wrench size={16} />} label="Labour logged" value={`${stats.totalLabourHours.toFixed(1)}h`} sub="billable technician time" />
        <StatCard icon={<DollarSign size={16} />} label="Parts billed" value={`$${stats.partsSpend.toFixed(2)}`} sub="not covered by warranty" />
        <StatCard icon={<ShieldCheck size={16} />} label="Parts covered" value={`$${stats.partsCoveredValue.toFixed(2)}`} sub="saved by active coverage" />
      </div>

      <div className="mt-8">
        <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold mb-3" style={{ color: "#5B6472" }}>Service Timeline</div>
        {jobs.length === 0 ? (
          <div className="bf-card p-8 text-center text-[13.5px]" style={{ color: "#8A93A1" }}>
            No service history yet.
          </div>
        ) : (
          <ol className="relative border-l-2 ml-3 space-y-3" style={{ borderColor: "#E7EAEE" }} data-testid="timeline">
            {jobs.map(j => {
              const st = j.priority === "equipment_down" && j.status !== "complete" ? "equipment_down" : j.status;
              const meta = STATUS_META[st] || STATUS_META.scheduled;
              const tech = techs.find(t => t.id === j.assigned_tech_id);
              const parts = j.parts_used || [];
              const cost = parts.reduce((a, p) => a + (p.covered ? 0 : (Number(p.unit_price) || 0) * (Number(p.qty) || 1)), 0);
              return (
                <li key={j.id} className="ml-5" data-testid={`timeline-${j.ticket_number}`}>
                  <span className="absolute -left-[9px] mt-1 h-4 w-4 rounded-full border-2 bg-white" style={{ borderColor: meta.color }} />
                  <div
                    className="block bf-card cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/dispatch")}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") navigate("/dispatch");
                    }}
                    data-testid={`timeline-card-${j.ticket_number}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="mono text-[12.5px] font-semibold">{j.ticket_number}</div>
                        <span className="bf-chip" style={{ background: meta.chipBg, color: meta.chipText }}>
                          <span className="bf-dot" style={{ background: meta.chipText }} />
                          {meta.label}
                        </span>
                        {j.priority === "equipment_down" && <span className="bf-chip" style={{ background: "#FDECEC", color: "#B42318" }}>DOWN</span>}
                      </div>
                      {j.status === "complete" && (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${j.id}/report.pdf`}
                          className="inline-flex items-center gap-1 text-[11px]"
                          style={{ color: "#2563EB" }}
                          data-testid={`download-job-report-${j.ticket_number}`}
                        >
                          <Download size={13} /> PDF
                        </a>
                      )}
                      <div className="mono text-[11px]" style={{ color: "#5F6B7A" }}>{fmtTimeAST(j.completed_at || j.created_at)}</div>
                    </div>
                    <div className="mt-2 text-[13.5px]" style={{ color: "#12161B", lineHeight: 1.5 }}>{j.description}</div>
                    {j.work_performed && (
                      <div className="mt-1 text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                        <span className="font-condensed uppercase tracking-wider text-[10px] font-semibold mr-1">Work</span>
                        {j.work_performed}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[12px] mono" style={{ color: "#5F6B7A" }}>
                      {tech && <span>👤 {tech.name}</span>}
                      {j.labour_hours > 0 && <span>⏱ {j.labour_hours}h</span>}
                      {parts.length > 0 && <span>🧰 {parts.length} part{parts.length !== 1 ? "s" : ""}</span>}
                      {cost > 0 && <span style={{ color: "#14181C" }}>$ {cost.toFixed(2)}</span>}
                      {parts.some(p => p.covered) && <span style={{ color: "#15803D" }}>covered</span>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {canEdit && eq && (
        <EditEquipmentDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          equipment={eq}
          tree={tree}
          onSaved={async () => { setEditOpen(false); await load(); toast.success("Equipment record updated"); }}
        />
      )}
    </div>
  );
}

function EditEquipmentDialog({ open, onOpenChange, equipment, tree, onSaved }) {
  const [form, setForm] = useState({});
  const [serialError, setSerialError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSerialError("");
    setForm({
      area_id: equipment.area_id || "",
      type: equipment.type || "",
      make: equipment.make || "",
      model: equipment.model || "",
      serial_number: equipment.serial_number || "",
      install_date: equipment.install_date || "",
      access_notes: equipment.access_notes || "",
      status: equipment.status || "",
    });
  }, [open, equipment]);

  const areaOptions = useMemo(() => tree.areas.map(a => {
    const s = tree.sites.find(x => x.id === a.site_id);
    const c = tree.customers.find(x => x.id === s?.customer_id);
    return { id: a.id, label: `${c?.name || "—"} → ${s?.name || "—"} → ${a.name}` };
  }).sort((x, y) => x.label.localeCompare(y.label)), [tree]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const save = async () => {
    setSaving(true);
    setSerialError("");
    try {
      await client.patch(`/equipment/${equipment.id}`, form);
      await onSaved();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Could not save the record";
      if (error?.response?.status === 409) setSerialError(detail);
      else toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid="edit-equipment-form">
        <DialogHeader>
          <DialogTitle>Edit equipment record</DialogTitle>
          <DialogDescription>Correct the details of this unit or move it to another area.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Area" className="md:col-span-2">
            <select
              className="w-full h-9 rounded-[10px] px-2 text-[13.5px] bg-white"
              style={{ border: "1px solid #E7EAEE" }}
              value={form.area_id || ""}
              onChange={e => set("area_id", e.target.value)}
              data-testid="edit-equipment-area"
            >
              {areaOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <Input className="rounded-[10px]" value={form.type || ""} onChange={e => set("type", e.target.value)} data-testid="edit-equipment-type" />
          </Field>
          <Field label="Make">
            <Input className="rounded-[10px]" value={form.make || ""} onChange={e => set("make", e.target.value)} data-testid="edit-equipment-make" />
          </Field>
          <Field label="Model">
            <Input className="rounded-[10px]" value={form.model || ""} onChange={e => set("model", e.target.value)} data-testid="edit-equipment-model" />
          </Field>
          <Field label="Serial number">
            <Input
              className="rounded-[10px] mono"
              style={serialError ? { borderColor: "#B42318" } : undefined}
              value={form.serial_number || ""}
              onChange={e => { set("serial_number", e.target.value); setSerialError(""); }}
              data-testid="edit-equipment-serial"
            />
            {serialError && (
              <div className="text-[12px] mt-1" style={{ color: "#B42318" }} data-testid="edit-equipment-serial-error">
                {serialError}
              </div>
            )}
          </Field>
          <Field label="Install date">
            <Input className="rounded-[10px] mono" placeholder="YYYY-MM-DD" value={form.install_date || ""} onChange={e => set("install_date", e.target.value)} data-testid="edit-equipment-install-date" />
          </Field>
          <Field label="Access notes">
            <Input className="rounded-[10px]" value={form.access_notes || ""} onChange={e => set("access_notes", e.target.value)} data-testid="edit-equipment-access-notes" />
          </Field>
          <Field label="Status">
            <select className="w-full h-9 rounded-[10px] px-2 text-[13.5px] bg-white" style={{ border: "1px solid #E7EAEE" }}
                    value={form.status || ""} onChange={e => set("status", e.target.value)} data-testid="edit-equipment-status">
              {EQUIPMENT_STATUS_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </Field>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" className="bf-btn-ghost" onClick={() => onOpenChange(false)} data-testid="edit-equipment-cancel">Cancel</Button>
          <Button className="bf-btn-primary px-4" onClick={save} disabled={saving} data-testid="edit-equipment-save">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="bf-card">
      <div className="flex items-center gap-1.5 font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        <span style={{ color: "#2563EB" }}>{icon}</span>{label}
      </div>
      <div className="text-[30px] font-semibold mt-1.5 mono" style={{ color: "#12161B", letterSpacing: "-0.02em", lineHeight: 1.1 }}>{value}</div>
      <div className="text-[12px] mt-1" style={{ color: "#5B6472" }}>{sub}</div>
    </div>
  );
}
